/* eslint-disable react/no-children-prop */
import { useEffect, useState } from 'react';
import { Button, DrawerHeader, Loading, Text } from 'components/styleguide';
import { useRootTranslation } from 'utils/hooks';
import { Divider, Grid } from '@material-ui/core';
import { Trans } from 'react-i18next';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useDispatch } from 'react-redux';
import { validateImportFileCall } from 'redux-core/import-tracks-spreadsheet/services';
import { setResolverStepOnDialog } from 'redux-core/import-tracks-spreadsheet/actions';
import {
  startUnmatchedPrefetch,
  cancelUnmatchedPrefetch,
} from 'redux-core/import-tracks-spreadsheet/unmatchedPrefetch';
import enhancer from './enhancer';
import ResolverStep from './ResolverStep';
import FileDropZone from './FileDropZone';
import ErrorOnSpreadsheet from './ErrorOnSpreadsheet';
import ReadyToImport from './ReadyToImport';

const rootT = 'drawers.importTracksSpreadsheetDrawer';

const FormatedLiTagComponent = ({ children }) => (
  <li>
    <Text variant="formLabel" children={children} />
  </li>
);

const ImportTracksSpreadsheetDrawer = ({
  onClose,
  getTemplateFile,
  currentUserId,
  productionId,
  divisionId,
  ...props
}) => {
  const cueContainerId = props.payload.id;
  const t = useRootTranslation(rootT);
  const dispatch = useDispatch();

  const [step, setStep] = useState('upload');
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [importJobId, setImportJobId] = useState(null);
  const [readyImport, setReadyImport] = useState(null);
  const [s3FileKey, setS3FileKey] = useState(null);
  const [validSpreadsheet, setValidSpreadsheet] = useState({
    state: null,
    error: null,
  });

  const downloadTemplate = async () => {
    await getTemplateFile();
  };

  /* QW-2408: every import runs the qwire-import (split) engine — Import opens the
     resolver, which pauses at the match boundary so unmatched rows are resolved in-app
     instead of silently skipped. QW-129: a fully-matched import auto-finishes inside
     ResolverStep, so this step only ever shows a spinner for it — never the grid. The
     legacy one-shot path is no longer reachable from the UI (the engine stays selectable
     server-side via TRACKS_IMPORT_ENGINE for the parity harness). */
  const handleImportClick = () => {
    if (importJobId) {
      setStep('resolve');
      dispatch(setResolverStepOnDialog());
    }
  };

  useEffect(() => {
    /* QW-2408: the engine is bound to the job at /validate. The UI always requests
       the split (resolver) pipeline explicitly — the server's omitted-engine fallback
       is also qwire-import post-cutover; legacy is opt-in only (the M3 parity harness
       sends engine:'legacy'). */
    const engine = 'qwire-import';
    let startedJobId = null;
    let cancelled = false;

    const validate = async () => {
      const {
        status,
        statusReason,
        id: jobId,
      } = await validateImportFileCall({
        s3FileKey,
        userId: currentUserId,
        productionId,
        cueContainerId,
        engine,
      });

      const isValid = status === 'FILE_STRUCTURE_VALID';

      setReadyImport(isValid);

      setValidSpreadsheet({
        error: statusReason,
        state: isValid,
      });

      if (isValid) {
        setImportJobId(jobId);

        if (engine === 'qwire-import' && !cancelled) {
          startUnmatchedPrefetch(jobId);
          startedJobId = jobId;
        }
      }

      setIsProcessingFile(false);
    };

    if (s3FileKey !== null) {
      // Call backend to validate file
      validate();
    }

    return () => {
      cancelled = true;
      if (startedJobId) cancelUnmatchedPrefetch(startedJobId);
    };
  }, [s3FileKey, cueContainerId, currentUserId, productionId]);

  if (step === 'resolve') {
    return <ResolverStep jobId={importJobId} divisionId={divisionId} onClose={onClose} />;
  }

  return (
    <>
      <DrawerHeader bold hideBackButton onClose={onClose} title={t('title')}>
        <Button
          alwaysEnabled
          children={t('cancel')}
          onClick={onClose}
          size="xs"
          synchronous
          testId="save-as-template-cancel"
          variant="destructive"
        />
        <Button
          children={t('import')}
          disabled={!readyImport}
          onClick={handleImportClick}
          size="sm"
          testId="save-as-template-save"
          type="submit"
          variant="primary"
        />
      </DrawerHeader>
      <Grid container spacing={2}>
        <Divider sx={{ my: 2 }} />
        <Grid item xs={9}>
          <Text children={t('apresentation')} />
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            endIcon={<FileDownloadIcon />}
            size="sm"
            variant="text"
            children={t('downloadTemplate')}
            onClick={downloadTemplate}
            type="button"
          />
        </Grid>
        <Grid container direction="column">
          <Text children={t('steps.title')} bold />
          <Grid item>
            <Trans i18nKey={`${rootT}.steps.step1`}>
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
            </Trans>
          </Grid>
          <Grid item>
            <Trans i18nKey={`${rootT}.steps.step2`}>
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
              <ul style={{ marginTop: 0, marginBottom: 8 }}>
                <FormatedLiTagComponent />
                <FormatedLiTagComponent />
              </ul>
            </Trans>
          </Grid>
          <Grid item>
            <Trans i18nKey={`${rootT}.steps.step3`}>
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
            </Trans>
          </Grid>
        </Grid>
      </Grid>
      {isProcessingFile && <Loading />}
      {readyImport === null && !isProcessingFile && (
        <FileDropZone setS3FileKey={setS3FileKey} onUploadingChange={setIsProcessingFile} />
      )}
      {readyImport && <ReadyToImport />}
      {readyImport === false && validSpreadsheet.state === false && (
        <ErrorOnSpreadsheet
          error={validSpreadsheet.error}
          setReadyImport={setReadyImport}
          setValidSpreadsheet={setValidSpreadsheet}
        />
      )}
    </>
  );
};

export default enhancer(ImportTracksSpreadsheetDrawer);
